import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FlightItineraryGroupNode } from '@app/generated/graphql';
import { DropdownItem, DropdownV2Component } from '@voyagu/radar-design-system';
import { distinctUntilChanged } from 'rxjs';
import { FormlyTypeaheadV2Component } from '../typeahead-v2/formly-typeahead-v2.component';
import {
  getFlightItineraryDepartureAt,
  getFlightItineraryRoute,
  getFlightItineraryStopsLabel,
} from './option-select.utils';

@Component({
  selector: 'option-select',
  templateUrl: './option-select.component.html',
  styleUrls: ['./option-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionSelectComponent extends FormlyTypeaheadV2Component implements OnInit {
  changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('optionDropdown') optionDropdown?: DropdownV2Component;

  createNewSelected = true;
  selectedOrderIds = new Set<string>();

  getFlightItineraryRoute = getFlightItineraryRoute;
  getFlightItineraryDepartureAt = getFlightItineraryDepartureAt;
  getFlightItineraryStopsLabel = getFlightItineraryStopsLabel;

  ngOnInit() {
    super.ngOnInit();
    this.applyFormValue(this.formControl.value as string[] | string | null | undefined);

    this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev || []) === JSON.stringify(curr || []),
        ),
      )
      .subscribe((value) => {
        this.applyFormValue(value as string[] | string | null | undefined);
        this.changeDetectorRef.markForCheck();
      });
  }

  applyFormValue(value: string[] | string | null | undefined) {
    const ids = Array.isArray(value)
      ? value.map(String).filter(Boolean)
      : value
        ? [String(value)]
        : [];

    const sameSelection =
      ids.length === this.selectedOrderIds.size && ids.every((id) => this.selectedOrderIds.has(id));
    const sameMode = ids.length ? !this.createNewSelected : this.createNewSelected;

    if (sameSelection && sameMode) {
      return;
    }

    this.selectedOrderIds.clear();

    if (ids.length) {
      ids.forEach((id) => this.selectedOrderIds.add(id));
      this.createNewSelected = false;
      this.updateSelectedItemLabel();
      return;
    }

    this.createNewSelected = true;
    this.selectedItem = { label: 'Create new option', value: '' };
  }

  getOptionItems(items: DropdownItem[] | null | undefined) {
    return (items || []).filter((item) => item.value);
  }

  selectCreateNew() {
    this.createNewSelected = true;
    this.selectedOrderIds.clear();
    this.syncFormControl();
    this.selectedItem = { label: 'Create new option', value: '' };

    if (!this.optionDropdown?.collapsed) {
      this.optionDropdown?.toggleCollapsed();
    }

    this.changeDetectorRef.markForCheck();
  }

  toggleOption(item: DropdownItem, event: Event) {
    event.stopPropagation();

    const id = String(item.value);

    if (this.selectedOrderIds.has(id)) {
      this.selectedOrderIds.delete(id);
    } else {
      this.selectedOrderIds.add(id);
      this.createNewSelected = false;
    }

    if (!this.selectedOrderIds.size) {
      this.createNewSelected = true;
      this.selectedItem = { label: 'Create new option', value: '' };
    } else {
      this.updateSelectedItemLabel();
    }

    this.syncFormControl();
    this.changeDetectorRef.markForCheck();
  }

  isOptionChecked(item: DropdownItem) {
    return this.selectedOrderIds.has(String(item.value));
  }

  trackFlightItinerary(_index: number, group: FlightItineraryGroupNode) {
    return `${group.departureAirport.locationCode}-${group.arrivalAirport.locationCode}-${String(group.departureAt)}`;
  }

  syncFormControl() {
    if (this.createNewSelected) {
      this.formControl.setValue([]);
    } else {
      this.formControl.setValue(Array.from(this.selectedOrderIds));
    }

    this.formControl.markAsDirty();
  }

  updateSelectedItemLabel() {
    const ids = Array.from(this.selectedOrderIds);

    if (!ids.length) {
      this.selectedItem = null;
      return;
    }

    this.selectedItem = {
      label: ids.map((id) => `Option ${id}`).join(', '),
      value: ids.join(','),
    };
  }
}
